import { useEffect, useRef, useState } from 'react';
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Folder, X } from 'lucide-react';

import { useTranslation } from '@sshop/i18n';
import type { CategoryNode } from '@sshop/domain';

import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';

import { categoriesMenuStyles as s } from './CategoriesMenu.styles';
import { CategoriesMenuPromoCard } from './CategoriesMenuPromoCard';
import type { CategoriesMobileDrawerProps } from './CategoriesMenu.types';

export function CategoriesMobileDrawer({
  roots,
  promos,
  open,
  onClose,
}: CategoriesMobileDrawerProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  // Stack of nodes the user has drilled into (path), excluding the implicit root level.
  const [stack, setStack] = useState<readonly CategoryNode[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const promoDragRef = useRef({
    active: false,
    moved: false,
    pointerId: -1,
    startX: 0,
    scrollLeft: 0,
  });

  // Reset stack whenever drawer is closed so re-opening starts fresh at root.
  useEffect(() => {
    if (!open) setStack([]);
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [stack]);

  // Body scroll lock + Escape to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const current = stack[stack.length - 1] ?? null;
  const items: readonly CategoryNode[] = current ? current.children : roots;
  const title = current ? current.name : t('nav.categories');

  const pop = () => setStack((s) => s.slice(0, -1));
  const push = (node: CategoryNode) => setStack((s) => [...s, node]);

  const startPromoDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    // A previous drag leaves `moved` set until the following click is
    // suppressed. Always reset it on a fresh gesture, including touch taps.
    promoDragRef.current.moved = false;

    // Keep CTA links fully interactive. Mouse users can still drag from the
    // rest of the card, while touch devices retain native horizontal scroll.
    const target = event.target;
    if (
      event.pointerType !== 'mouse' ||
      event.button !== 0 ||
      (target instanceof Element && target.closest('a, button'))
    ) {
      promoDragRef.current.active = false;
      promoDragRef.current.pointerId = -1;
      return;
    }
    promoDragRef.current = {
      active: true,
      moved: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: event.currentTarget.scrollLeft,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const movePromoDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = promoDragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    const distance = event.clientX - drag.startX;
    if (Math.abs(distance) > 4) drag.moved = true;
    event.currentTarget.scrollLeft = drag.scrollLeft - distance;
  };

  const endPromoDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = promoDragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    drag.active = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const cancelDraggedPromoClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!promoDragRef.current.moved) return;
    event.preventDefault();
    event.stopPropagation();
    promoDragRef.current.moved = false;
  };

  const scrollPromosWithWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const track = event.currentTarget;
    if (
      track.scrollWidth <= track.clientWidth ||
      Math.abs(event.deltaY) <= Math.abs(event.deltaX)
    ) {
      return;
    }
    event.preventDefault();
    track.scrollLeft += event.deltaY;
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className={s.drawerScrim} onClick={onClose} aria-hidden />
      <aside className={s.drawer} role="dialog" aria-modal="true" aria-label={t('nav.categories')}>
        <div className={s.drawerHeader}>
          {stack.length > 0 ? (
            <button type="button" className={s.drawerBack} onClick={pop} aria-label={t('nav.back')}>
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
          ) : (
            <span className={s.drawerBack} aria-hidden />
          )}
          <h2 className={s.drawerTitle}>{title}</h2>
          <button
            type="button"
            className={s.drawerClose}
            onClick={onClose}
            aria-label={t('nav.close')}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {stack.length === 0 && promos.length > 0 ? (
          <section className={s.drawerPromoShelf} aria-label={t('nav.categoriesFeatured')}>
            <div
              className={s.drawerPromoTrack}
              tabIndex={promos.length > 1 ? 0 : undefined}
              onPointerDown={startPromoDrag}
              onPointerMove={movePromoDrag}
              onPointerUp={endPromoDrag}
              onPointerCancel={endPromoDrag}
              onClickCapture={cancelDraggedPromoClick}
              onWheel={scrollPromosWithWheel}
              onDragStart={(event) => event.preventDefault()}
            >
              {promos.map((banner) => (
                <CategoriesMenuPromoCard
                  key={banner.id}
                  banner={banner}
                  compact
                  onNavigate={onClose}
                />
              ))}
            </div>
          </section>
        ) : null}

        <div ref={bodyRef} className={s.drawerBody}>
          {current ? (
            <Link
              to={routes.catalog(locale, current.slug)}
              onClick={onClose}
              className={s.drawerListItem}
              style={{ fontWeight: 600 }}
            >
              <span className={s.drawerThumb} aria-hidden>
                <ArrowRight className="h-4 w-4" />
              </span>
              <span className={s.drawerLabel}>
                {t('nav.categoriesViewAllIn', { name: current.name })}
              </span>
            </Link>
          ) : null}

          <ul>
            {items.map((node) => {
              // Cap navigation at depth 2: only allow drilling from root level.
              // At sub level (stack.length > 0) every item links directly to its listing.
              const canDrill = stack.length === 0 && node.children.length > 0;
              return (
                <li key={node.id}>
                  {canDrill ? (
                    <button type="button" className={s.drawerListItem} onClick={() => push(node)}>
                      <span className={s.drawerThumb} aria-hidden>
                        {(node.iconUrl ?? node.imageUrl) ? (
                          <img
                            src={node.iconUrl ?? node.imageUrl ?? undefined}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Folder className="h-4 w-4" />
                        )}
                      </span>
                      <span className={s.drawerLabel}>{node.name}</span>
                      {node.productCount > 0 ? (
                        <span className={s.drawerMeta}>{node.productCount}</span>
                      ) : null}
                      <ChevronRight className={s.drawerChevron} aria-hidden />
                    </button>
                  ) : (
                    <Link
                      to={routes.catalog(locale, node.slug)}
                      className={s.drawerListItem}
                      onClick={onClose}
                    >
                      <span className={s.drawerThumb} aria-hidden>
                        {(node.iconUrl ?? node.imageUrl) ? (
                          <img
                            src={node.iconUrl ?? node.imageUrl ?? undefined}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Folder className="h-4 w-4" />
                        )}
                      </span>
                      <span className={s.drawerLabel}>{node.name}</span>
                      {node.productCount > 0 ? (
                        <span className={s.drawerMeta}>{node.productCount}</span>
                      ) : null}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className={s.drawerCta}>
          <Link to={routes.categories(locale)} className={s.drawerCtaLink} onClick={onClose}>
            {t('home.categories.viewAll')}
          </Link>
        </div>
      </aside>
    </>,
    document.body,
  );
}
